import { Button } from '../ui/button';

/**
 * getSarStatus — the deadline logic: is this on the user or on them, what
 * (if anything) they can do about it right now, and — via
 * `getNextAutomaticStep` — what the system will do next on its own if they
 * do nothing, so the hero panel's sentence and its action buttons can never
 * disagree with each other.
 *
 *   overdue    breach-wash   "They are 9 days past the legal deadline"
 *   needs id   amber-wash    "They have asked for proof of who you are"
 *   waiting    ground        "They have 14 days left to reply"
 *   received   brand-wash    "Your records have arrived"
 *
 * Copy is plain English; automatic things are stated as facts.
 */
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const DAY_MS = 24 * 60 * 60 * 1000;

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
};

/**
 * What the system will do next, and roughly when — mirroring the
 * scheduler's own thresholds (`services/scheduler.py`) against
 * `deadline.reminders_sent`, so this is never a promise the backend isn't
 * actually keeping: the earliest of the reminder types it hasn't fired yet
 * (the 14-day "no reply at all" chase, 7-days-before, on-the-day, and
 * 7-days-overdue), or null once every automatic step has already happened.
 */
const getNextAutomaticStep = ({ sar, deadline, emailThread, effective }) => {
  if (!['sent', 'awaiting_response', 'overdue', 'partial'].includes(effective)) return null;
  const sentAt = sar?.sent_at || deadline?.sent_date;
  const dueStr = deadline?.extended_deadline || deadline?.initial_deadline;
  if (!sentAt || !dueStr) return null;

  const sent = new Date(sentAt);
  const due = new Date(dueStr);
  const remindersSent = deadline?.reminders_sent || [];
  const hasInbound = (emailThread || []).some((e) => e.direction === 'incoming');

  const candidates = [];
  if (!hasInbound && !remindersSent.includes('chase_14d')) {
    candidates.push({ date: new Date(sent.getTime() + 14 * DAY_MS), text: "check in with them, since they haven't replied at all" });
  }
  if (!remindersSent.includes('7_days')) {
    candidates.push({ date: new Date(due.getTime() - 7 * DAY_MS), text: 'remind them' });
  }
  if (!remindersSent.includes('due')) {
    candidates.push({ date: due, text: 'follow up with them, since it will be due' });
  }
  if (!remindersSent.includes('overdue_warning')) {
    candidates.push({ date: new Date(due.getTime() + 7 * DAY_MS), text: 'send them a further notice' });
  }
  if (!candidates.length) return null;

  candidates.sort((a, b) => a.date - b.date);
  const next = candidates[0];
  return {
    text: next.text,
    when: next.date > new Date() ? `on ${longDate(next.date)}` : 'very soon',
  };
};

/**
 * The one email in the thread that's still waiting on an ID reply, if any —
 * shared between getSarStatus (the top-of-page hero) and EmailTimeline (the
 * inline prompt on that exact message), so the two can never disagree about
 * which request is pending or whether it's already been answered.
 */
export const findPendingIdRequest = (emailThread) => {
  const pendingIdRequest = emailThread?.find(
    (e) =>
      e.ai_classification === 'id_request' &&
      !e.user_action_taken &&
      !e.ai_auto_responded &&
      !e.id_sent_automatically,
  );
  const idRequestIndex = pendingIdRequest
    ? emailThread.findIndex((e) => e.id === pendingIdRequest.id)
    : -1;
  const idAlreadySent =
    idRequestIndex >= 0 &&
    emailThread.slice(idRequestIndex + 1).some((e) => e.direction === 'outgoing' && e.id_documents_attached);
  return { pendingIdRequest, idAlreadySent };
};

export const getSarStatus = ({
  sar,
  emailThread,
  deadline,
  hasIdDocuments,
  isReadyForIdRequests,
  onSendReminder,
  onShowIco,
}) => {
  const effective = sar?.effective_status || sar?.status || 'draft';
  const daysRemaining = typeof sar?.days_remaining === 'number' ? sar.days_remaining : null;
  const isExtended = Boolean(deadline?.extended_deadline);
  const org = sar?.organisation_name || 'The organisation';

  const { pendingIdRequest, idAlreadySent } = findPendingIdRequest(emailThread);
  const needsId = Boolean(pendingIdRequest) && !idAlreadySent && effective !== 'complete';
  const nextStep = getNextAutomaticStep({ sar, deadline, emailThread, effective });

  let kind;
  let headline;
  let body;
  let actions = null;

  if (effective === 'draft') {
    kind = 'draft';
    headline = 'Not sent yet';
    body = 'Check the letter below, then send it. The one-month clock starts when they receive it.';
  } else if (effective === 'complete') {
    kind = 'received';
    headline = 'Your records have arrived';
    body = `${org} has sent what they hold about you. It is in your Documents, locked with your password.`;
  } else if (effective === 'escalated_ico') {
    kind = 'ico';
    headline = 'This is with the ICO';
    body = 'You have complained to the Information Commissioner’s Office. Keep anything else the organisation sends you as evidence. Nothing else is needed from you for now.';
  } else if (needsId) {
    kind = 'needs-id';
    headline = 'They have asked for proof of who you are';
    // The actual send/upload action lives inline on their message in the
    // correspondence below (see EmailTimeline), right where the request
    // was made, rather than duplicated up here — this card just explains
    // the situation.
    body = isReadyForIdRequests
      ? `${org} needs to check your identity before releasing anything. Your ID is uploaded and you have let us send it automatically — if it hasn't gone within a few minutes, you can send it yourself from their message below.`
      : hasIdDocuments
        ? `${org} needs to check your identity before releasing anything. Your ID is uploaded — reply from their message below to send it, or turn on automatic sending in Identity documents.`
        : `${org} needs to check your identity before releasing anything. Upload a photo of your ID from their message below and we will send it for you.`;
  } else if (effective === 'overdue') {
    kind = 'overdue';
    const late = daysRemaining !== null ? Math.abs(daysRemaining) : null;
    headline = late !== null
      ? `They are ${plural(late, 'day')} past the legal deadline`
      : 'They are past the legal deadline';
    body = `${org} had one month to reply and has not. You can send them a reminder now${nextStep ? `, or leave it — we'll automatically ${nextStep.text} ${nextStep.when} if nothing changes before then` : ''}. If they still do not reply, you can complain to the Information Commissioner’s Office (ICO). Complaining is free and you do not need a solicitor.`;
    actions = (
      <>
        <Button variant="breach" onClick={onSendReminder} data-testid="send-reminder-btn">
          Send a reminder
        </Button>
        <Button variant="secondary" onClick={onShowIco} data-testid="ico-how-to-btn">
          How to complain to the ICO
        </Button>
      </>
    );
  } else if (effective === 'partial') {
    kind = 'partial';
    headline = daysRemaining !== null && daysRemaining >= 0
      ? `Some records have arrived. They have ${plural(daysRemaining, 'day')} left to send the rest`
      : 'Some records have arrived, but not all of them';
    body = `Look at what has come through in Documents. If anything is missing, reply to them below and ask for it.${nextStep ? ` We'll automatically ${nextStep.text} ${nextStep.when} if the rest doesn't arrive by then.` : ''}`;
  } else {
    kind = 'waiting';
    if (daysRemaining !== null && daysRemaining >= 0) {
      headline = isExtended
        ? `They asked for more time and now have ${plural(daysRemaining, 'day')} left`
        : `They have ${plural(daysRemaining, 'day')} left to reply`;
    } else {
      headline = 'They have one month to reply';
    }
    body = `Nothing is needed from you. We check the inbox every few minutes${nextStep ? `, and will automatically ${nextStep.text} ${nextStep.when} if you haven't heard back by then` : ''}. You will hear from us the moment they reply.`;
  }

  return { kind, headline, body, actions };
};

const SURFACE = {
  overdue: 'bg-breach-wash',
  ico: 'bg-breach-wash',
  'needs-id': 'bg-amber-wash',
  received: 'bg-brand-wash',
  waiting: 'bg-ground',
  partial: 'bg-ground',
  draft: 'bg-ground',
};
const HEADLINE_COLOUR = {
  overdue: 'text-breach-ink',
  ico: 'text-breach-ink',
  'needs-id': 'text-amber-ink',
  received: 'text-brand-ink',
  waiting: 'text-ink',
  partial: 'text-ink',
  draft: 'text-ink',
};

/**
 * StatusCard — the headline panel: is this on the user or on them, in one
 * sentence, plus whatever the status actually calls for (send a reminder,
 * how to complain to the ICO…) right there — `getSarStatus` decides both,
 * this just lays them out. There is no separate "what you can do" card;
 * message-specific actions (send my ID, ask for help with a refusal) live
 * on the message itself in the correspondence below instead.
 */
const StatusCard = ({ kind, headline, body, actions }) => (
  <section
    className={`rounded-lg px-6 py-5 ${SURFACE[kind]}`}
    data-testid={`status-card-${kind}`}
    aria-labelledby="deadline-headline"
  >
    <h2 id="deadline-headline" className={`text-[16px] font-semibold leading-snug ${HEADLINE_COLOUR[kind]}`}>
      {headline}
    </h2>
    <p className="mt-2 text-[15px] leading-[1.6] text-ink-70 max-w-[60ch]">{body}</p>
    {actions && <div className="mt-4 flex flex-wrap gap-2.5">{actions}</div>}
  </section>
);

export default StatusCard;
